import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  FolderOpen as FolderIcon,
  Description as DocumentIcon,
  Warning as WarningIcon,
  CheckCircle as ValidIcon,
  Error as ExpiredIcon,
  Visibility as ViewIcon,
  CalendarMonth as CalendarIcon,
  Badge as BadgeIcon,
  Work as WorkIcon,
  LocalHospital as MedicalIcon,
  School as EducationIcon,
  Assignment as CertificateIcon,
  Home as HomeIcon 
} from '@mui/icons-material';
import api from '../services/api';

const EmployeeDocuments = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Document statistics
  const [stats, setStats] = useState({
    totalDocuments: 0,
    validDocuments: 0,
    expiringDocuments: 0,
    expiredDocuments: 0
  });

  // Document categories
  const categories = {
    passport: { icon: <BadgeIcon />, color: '#1976d2', label: 'Identity' },
    id_card: { icon: <BadgeIcon />, color: '#1976d2', label: 'Identity' },
    work_permit: { icon: <WorkIcon />, color: '#9c27b0', label: 'Work Permit' },
    residence_permit: { icon: <HomeIcon />, color: '#ff9800', label: 'Residence' },
    medical_certificate: { icon: <MedicalIcon />, color: '#f44336', label: 'Medical' },
    education: { icon: <EducationIcon />, color: '#4caf50', label: 'Education' },
    certificate: { icon: <CertificateIcon />, color: '#795548', label: 'Certificate' },
    contract: { icon: <DocumentIcon />, color: '#607d8b', label: 'Contract' },
    other: { icon: <FolderIcon />, color: '#9e9e9e', label: 'Other' }
  };

  useEffect(() => {
    const loggedIn = localStorage.getItem('employeeLoggedIn');
    if (!loggedIn) {
      navigate('/employee/login');
      return;
    }
    fetchDocuments();
  }, [navigate]);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/employee/documents');
      const docs = response.data.documents || [];
      setDocuments(docs);
      
      // Calculate stats
      const now = new Date();
      const thirtyDays = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
      
      const stats = {
        totalDocuments: docs.length,
        validDocuments: docs.filter(d => !d.expiry_date || new Date(d.expiry_date) > now).length,
        expiringDocuments: docs.filter(d => {
          if (!d.expiry_date) return false;
          const expiry = new Date(d.expiry_date);
          return expiry > now && expiry <= thirtyDays;
        }).length,
        expiredDocuments: docs.filter(d => d.expiry_date && new Date(d.expiry_date) <= now).length
      };
      
      setStats(stats);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
      setLoading(false);
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const response = await api.get(`/employee/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `document-${documentId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Failed to download document');
    }
  };

  const handleView = async (documentId) => {
    try {
      const response = await api.get(`/employee/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      
      const file = new Blob([response.data], { type: response.headers['content-type'] });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } catch (error) {
      setError('Failed to view document');
    }
  };

  const getDocumentStatus = (document) => {
    if (!document.expiry_date) {
      return { label: 'No Expiry', color: 'default', icon: <ValidIcon /> };
    }
    
    const now = new Date();
    const expiry = new Date(document.expiry_date);
    const daysUntilExpiry = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (expiry <= now) {
      return { label: 'Expired', color: 'error', icon: <ExpiredIcon /> };
    } else if (daysUntilExpiry <= 30) {
      return { label: `Expires in ${daysUntilExpiry} days`, color: 'warning', icon: <WarningIcon /> };
    } else {
      return { label: 'Valid', color: 'success', icon: <ValidIcon /> };
    }
  };

  const getCategoryInfo = (type) => {
    return categories[type] || categories.other;
  };

  const groupDocumentsByCategory = () => {
    const grouped = {};
    documents.forEach(doc => {
      const category = doc.document_type || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(doc);
    });
    return grouped;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const groupedDocuments = groupDocumentsByCategory();

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="fixed" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => navigate('/employee/dashboard')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            My Documents
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: 10, pb: 3 }}>
        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 600 }}>
                  {stats.totalDocuments}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Documents
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 600 }}>
                  {stats.validDocuments}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Valid
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 600 }}>
                  {stats.expiringDocuments}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expiring Soon
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 600 }}>
                  {stats.expiredDocuments}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expired
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Documents by Category */}
        {documents.length > 0 ? (
          Object.entries(groupedDocuments).map(([category, docs]) => {
            const categoryInfo = getCategoryInfo(category);
            return (
              <Paper key={category} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: 1, 
                    bgcolor: `${categoryInfo.color}20`,
                    color: categoryInfo.color,
                    mr: 2
                  }}>
                    {categoryInfo.icon}
                  </Box>
                  <Typography variant="h6">
                    {categoryInfo.label} Documents
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {docs.map((document) => {
                    const status = getDocumentStatus(document);
                    return (
                      <Grid item xs={12} md={6} lg={4} key={document.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Stack spacing={1}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                {document.document_name}
                              </Typography>
                              
                              <Chip
                                label={status.label}
                                color={status.color}
                                size="small"
                                icon={status.icon}
                              />
                              
                              {document.issue_date && (
                                <Typography variant="body2" color="text.secondary">
                                  <CalendarIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                  Issued: {new Date(document.issue_date).toLocaleDateString()}
                                </Typography>
                              )}
                              
                              {document.expiry_date && (
                                <Typography variant="body2" color="text.secondary">
                                  <CalendarIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                  Expires: {new Date(document.expiry_date).toLocaleDateString()}
                                </Typography>
                              )}
                              
                              <Typography variant="caption" color="text.secondary">
                                Uploaded: {new Date(document.uploaded_at).toLocaleDateString()}
                              </Typography>
                            </Stack>
                          </CardContent>
                          <CardActions>
                            <Button 
                              size="small" 
                              startIcon={<ViewIcon />}
                              onClick={() => handleView(document.id)}
                            >
                              View
                            </Button>
                            <Button 
                              size="small" 
                              startIcon={<DownloadIcon />}
                              onClick={() => handleDownload(document.id, document.file_name)}
                            >
                              Download
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            );
          })
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <FolderIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Documents Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your documents will appear here once they are uploaded by the HR department
            </Typography>
          </Paper>
        )}

        {/* Expiring Documents Alert */}
        {stats.expiringDocuments > 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="subtitle2">
              You have {stats.expiringDocuments} document(s) expiring within 30 days
            </Typography>
            <Typography variant="body2">
              Please ensure you renew them before expiry to maintain compliance
            </Typography>
          </Alert>
        )}
      </Container>
    </Box>
  );
};

export default EmployeeDocuments;
